<template>
  <div class="card card-body shadow-sm mt-0">
    <div class="form-header d-flex justify-content-between align-items-center">
      <h4 class="form-title">{{ pessoa.id ? 'Editar Pessoa Física' : 'Nova Pessoa Física' }}</h4>
      <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
    </div>
    <BaseForm :isFormValid="isFormValid" :isSubmitting="isSubmitting" @submit="salvarPessoa" @cancel="voltar">

      <!-- Dados Pessoais -->
      <FormSection title="Dados Pessoais">
        <InputField 
          v-model="pessoa.nome" 
          label="Nome <span class='required'>*</span>" 
          id="nome" 
          placeholder="Digite o nome" 
          :class="{ 'error-input': errors.nome }"
          width="400px"
          required
        />

        <InputField 
          v-model="pessoa.data_nascimento" 
          label="Data de Nascimento <span class='required'>*</span>" 
          id="data_nascimento" 
          type="date" 
          :class="{ 'error-input': errors.data_nascimento }"
          width="150px"
          required
        />

        <InputField 
          v-model="pessoa.cpf" 
          label="CPF <span class='required'>*</span>" 
          id="cpf" 
          placeholder="Digite o CPF" 
          mask="cpf" 
          :class="{ 'error-input': errors.cpf }"
          width="150px"
          required
        />
        <span v-show="errors.cpf" class="error-message">{{ errors.cpf }}</span>

        <InputField 
          v-model="pessoa.rg" 
          label="RG" 
          id="rg" 
          placeholder="Digite o RG"
        />
        <SelectField 
          v-model="pessoa.sexo" 
          label="Sexo <span class='required'>*</span>" 
          id="sexo" 
          :options="[
            { value: 'M', text: 'Masculino' },
            { value: 'F', text: 'Feminino' }
          ]"
          :class="{ 'error-input': errors.sexo }"

          required
        />
        <InputField 
          v-model="pessoa.nacionalidade" 
          label="Nacionalidade" 
          id="nacionalidade" 
          placeholder="Digite a nacionalidade"
        />
        <InputField 
          v-model="pessoa.naturalidade" 
          label="Naturalidade" 
          id="naturalidade" 
          placeholder="Digite a naturalidade"
        />
        <SwitchField
          v-model="pessoa.socio"
          label="Sócio"
          id="socio"
        />
      </FormSection>

      <!-- Contato -->
      <FormSection title="Contato">
        <template #headerActions>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            title="Copiar contato e endereço do responsável"
            @click="modalResponsavel = true"
          >
            <i class="bi bi-files"></i>
          </button>
        </template>

        <datalist id="ddi-options-pf">
          <option
            v-for="opt in ddiOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.text }}
          </option>
        </datalist>

        <div class="phone-row">
          <InputField
            v-model="pessoa.telefone_ddi"
            label="DDI"
            id="telefone_ddi"
            placeholder="+55"
            list="ddi-options-pf"
            maxlength="5"
            width="90px"
            :class="{ 'error-input': showTelefoneDdiError }"
            @blur="touched.telefone_ddi = true"
          />

          <InputField
            v-model="pessoa.telefone"
            label="Telefone principal"
            id="telefone"
            placeholder="Digite o telefone"
            :mask="telefoneMask"
            :maxlength="telefoneMaxlength"
            :class="{ 'error-input': showTelefoneError }"
            @blur="touched.telefone = true"
          />
        </div>

        <div class="phone-row">
          <InputField
            v-model="pessoa.celular_ddi"
            label="DDI"
            id="celular_ddi"
            placeholder="+55"
            list="ddi-options-pf"
            maxlength="5"
            width="90px"
            :class="{ 'error-input': showCelularDdiError }"
            @blur="touched.celular_ddi = true"
          />

          <InputField
            v-model="pessoa.celular"
            label="Celular / WhatsApp <span class='required'>*</span>"
            id="celular"
            placeholder="Digite o celular"
            :mask="celularMask"
            :maxlength="celularMaxlength"
            :class="{ 'error-input': showCelularError }"
            @blur="touched.celular = true"
          />
        </div>

        <InputField 
          v-model="pessoa.email" 
          label="Email <span class='required'>*</span>" 
          id="email" 
          placeholder="Digite o email"
        />
      </FormSection>

      <!-- Endereço -->
      <FormSection title="Endereço">
        <InputField 
          v-model="pessoa.cep" 
          label="CEP <span class='required'>*</span>"
          id="cep" 
          placeholder="Digite o CEP"
          mask="cep" 
        />
        <InputField 
          v-model="pessoa.endereco" 
          label="Endereço <span class='required'>*</span>" 
          id="endereco" 
          placeholder="Digite o endereço"
        />
        <InputField 
          v-model="pessoa.numero" 
          label="Número <span class='required'>*</span>" 
          id="numero"
        />
        <InputField 
          v-model="pessoa.bairro" 
          label="Bairro <span class='required'>*</span>" 
          id="bairro"
        />
        <InputField 
          v-model="pessoa.complemento" 
          label="Complemento" 
          id="complemento"
        />
        <SelectField 
          v-if="estados.length > 0" 
          v-model="pessoa.estado_cod" 
          label="Estado <span class='required'>*</span>" 
          id="estado" 
          :options="estados.map(e => ({ value: e.id, text: `${e.nome} (${e.sigla})` }))"
          @change="carregarMunicipios"
          :class="{ 'error-input': errors.estado_cod }"
        />

        <SelectField 
          v-if="municipios.length > 0" 
          v-model="pessoa.municipio_cod" 
          label="Município <span class='required'>*</span>" 
          id="municipio" 
          :options="municipios.map(m => ({ value: m.id, text: m.nome }))"
          :class="{ 'error-input': errors.municipio_cod }"
        />
      </FormSection>

      <!-- Observações -->
      <FormSection title="Observações">
        <textarea 
          v-model="pessoa.observacoes" 
          id="observacoes" 
          rows="3" 
          class="form-control"
          placeholder="Adicione observações"
        ></textarea>
      </FormSection>
    </BaseForm>
  </div>

  <!-- Modal de seleção do responsável -->
  <CarregarPessoaFisica
    :mostrar="modalResponsavel"
    tipo-pessoa="responsavel"
    @fechar="modalResponsavel = false"
    @selecionar="onSelecionarResponsavel"
  />

</template>

<script>
import { reactive, ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from '@/services/axios';
import InputField from '@/components/Layout/InputField.vue';
import SelectField from '@/components/Layout/SelectField.vue';
import FormSection from '@/components/Layout/FormSection.vue';
import BaseForm from '@/components/Layout/BaseForm.vue';
import SwitchField from '@/components/Layout/SwitchField.vue';
import CarregarPessoaFisica from '@/components/modals/CarregarPessoaFisica.vue'; // <= IMPORTANTE
import globalUtils from '@/utils/globalUtils.js';

export default {
  components: { InputField, SelectField, FormSection, BaseForm, SwitchField, CarregarPessoaFisica },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const pessoa = reactive({
      nome: '',
      cpf: '',
      rg: '',
      data_nascimento: '',
      sexo: '',
      telefone_ddi: '+55',
      telefone: '',
      celular_ddi: '+55',
      celular: '',
      email: '',
      endereco: '',
      numero: '',
      complemento: '',
      bairro: '',
      estado_cod: '',
      municipio_cod: '',
      cep: '',
      nacionalidade: '',
      naturalidade: '',
      socio: false,
      observacoes: '',
    });

    const estados = ref([]);
    const municipios = ref([]);
    const errors = reactive({});
    const formTouched = ref(false);
    const isSubmitting = ref(false);

    // controla o modal de responsável
    const modalResponsavel = ref(false);

    // evita auto-preenchimento por CEP enquanto copiamos do responsável
    const initialLoadComplete = ref(false);

    const normalizeString = (str) => {
      return str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim() : '';
    };

    const touched = reactive({
      telefone_ddi: false,
      telefone: false,
      celular_ddi: false,
      celular: false,
    });

    const ddiOptions = [
      { value: '+55', text: 'Brasil +55' },
      { value: '+1', text: 'EUA/Canadá +1' },
      { value: '+351', text: 'Portugal +351' },
      { value: '+54', text: 'Argentina +54' },
      { value: '+598', text: 'Uruguai +598' },
      { value: '+595', text: 'Paraguai +595' },
      { value: '+56', text: 'Chile +56' },
      { value: '+39', text: 'Itália +39' },
      { value: '+34', text: 'Espanha +34' },
      { value: '+49', text: 'Alemanha +49' },
    ];

    const onTelefoneInput = (valor) => {
      const digits = limparTelefone(valor).slice(0, 11);
      pessoa.telefone = normalizarDdi(pessoa.telefone_ddi) === '+55'
        ? formatarTelefoneBrasil(digits)
        : valor;

      if (touched.telefone || formTouched.value) {
        errors.telefone = validarTelefone(pessoa.telefone);
      }
    };

    const onCelularInput = (valor) => {
      const digits = limparTelefone(valor).slice(0, 11);
      pessoa.celular = normalizarDdi(pessoa.celular_ddi) === '+55'
        ? formatarTelefoneBrasil(digits)
        : valor;

      if (touched.celular || formTouched.value) {
        errors.celular = validarCelular(pessoa.celular);
      }
    };

    const onTelefoneBlur = () => {
      touched.telefone = true;
      touched.telefone_ddi = true;
      errors.telefone_ddi = validarDdi(pessoa.telefone_ddi, !!pessoa.telefone);
      errors.telefone = validarTelefone(pessoa.telefone);
    };

    const onCelularBlur = () => {
      touched.celular = true;
      touched.celular_ddi = true;
      errors.celular_ddi = validarDdi(pessoa.celular_ddi, true);
      errors.celular = validarCelular(pessoa.celular);
    };    

    const limparTelefone = (valor) => (valor || '').replace(/\D/g, '');
    const normalizarDdi = (valor) => (valor || '').toString().trim();

    const formatarTelefoneBrasil = (digits) => {
      const numeros = (digits || '').replace(/\D/g, '').slice(0, 11);

      if (numeros.length <= 2) return numeros;
      if (numeros.length <= 6) return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
      if (numeros.length <= 10) {
        return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 6)}-${numeros.slice(6)}`;
      }

      return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`;
    };    

    const validarDdi = (valor, obrigatorio = false) => {
      const ddi = normalizarDdi(valor);
      if (!ddi) return obrigatorio ? 'DDI é obrigatório.' : '';
      if (!/^\+\d{1,4}$/.test(ddi)) return 'DDI deve estar no formato +55.';
      return '';
    };

    const validarTelefonePorDdi = (valor, ddi, obrigatorio = false, label = 'Telefone') => {
      const digits = limparTelefone(valor);

      if (!digits) return obrigatorio ? `${label} é obrigatório.` : '';

      if (normalizarDdi(ddi) === '+55') {
        if (![10, 11].includes(digits.length)) {
          return `${label} deve ter 10 ou 11 dígitos.`;
        }
        return '';
      }

      if (digits.length < 6 || digits.length > 15) {
        return `${label} internacional deve ter entre 6 e 15 dígitos.`;
      }

      return '';
    };

    const validarTelefone = (valor) =>
      validarTelefonePorDdi(valor, pessoa.telefone_ddi, false, 'Telefone principal');

    const validarCelular = (valor) =>
      validarTelefonePorDdi(valor, pessoa.celular_ddi, true, 'Celular / WhatsApp');

    watch(() => pessoa.cpf, (newCpf) => {
      const cpfNumeros = newCpf.replace(/\D/g, '');
      if (cpfNumeros.length < 11) {
        errors.cpf = '';
      } else if (cpfNumeros.length === 11) {
        errors.cpf = !globalUtils.validarCPF(newCpf) ? 'CPF inválido.' : '';
      }
    });

    watch(() => pessoa.cep, async (newCep) => {
      if (!initialLoadComplete.value) return;
      const cepLimpo = newCep ? newCep.replace(/\D/g, '') : '';
      if (cepLimpo.length === 8) {
        try {
          const enderecoData = await globalUtils.buscarEnderecoPorCEP(newCep);
          pessoa.endereco = enderecoData.endereco || '';
          pessoa.bairro = enderecoData.bairro || '';
          pessoa.complemento = enderecoData.complemento || '';

          const estadoEncontrado = estados.value.find(e =>
            e.sigla.toUpperCase() === (enderecoData.estado || '').toUpperCase()
          );
          if (estadoEncontrado) {
            pessoa.estado_cod = estadoEncontrado.id;
            await carregarMunicipios();
            const municipioEncontrado = municipios.value.find(m =>
              normalizeString(m.nome) === normalizeString(enderecoData.municipio)
            );
            pessoa.municipio_cod = municipioEncontrado ? municipioEncontrado.id : '';
          }
        } catch (error) {
          console.error("Erro ao buscar endereço pelo CEP:", error.message);
        }
      }
    });

    watch(() => [pessoa.telefone, pessoa.telefone_ddi], () => {
      if (touched.telefone || touched.telefone_ddi || formTouched.value) {
        errors.telefone_ddi = validarDdi(pessoa.telefone_ddi, !!pessoa.telefone);
        errors.telefone = validarTelefone(pessoa.telefone);
      } else {
        errors.telefone_ddi = '';
        errors.telefone = '';
      }
    });

    watch(() => [pessoa.celular, pessoa.celular_ddi], () => {
      if (touched.celular || touched.celular_ddi || formTouched.value) {
        errors.celular_ddi = validarDdi(pessoa.celular_ddi, true);
        errors.celular = validarCelular(pessoa.celular);
      } else {
        errors.celular_ddi = '';
        errors.celular = '';
      }
    });

    const isFormValid = computed(() => {
      return (
        pessoa.nome.trim() !== '' &&
        pessoa.cpf.trim() !== '' &&
        globalUtils.validarCPF(pessoa.cpf) &&
        pessoa.data_nascimento.trim() !== '' &&
        pessoa.sexo.trim() !== '' &&
        pessoa.celular.trim() !== '' &&
        !validarDdi(pessoa.telefone_ddi, !!pessoa.telefone) &&
        !validarDdi(pessoa.celular_ddi, true) &&
        !validarCelular(pessoa.celular) &&
        !validarTelefone(pessoa.telefone) &&
        pessoa.email.trim() !== '' &&
        pessoa.endereco.trim() !== '' &&
        (pessoa.numero !== null && pessoa.numero.toString().trim() !== '') &&
        pessoa.bairro.trim() !== '' &&
        pessoa.cep.trim() !== '' &&
        (pessoa.municipio_cod !== null && pessoa.municipio_cod.toString().trim() !== '') &&
        (pessoa.estado_cod !== null && pessoa.estado_cod.toString().trim() !== '') &&
        !Object.values(errors).some(error => error)
      );
    });

    const telefoneMask = computed(() => normalizarDdi(pessoa.telefone_ddi) === '+55' ? 'telefone' : null);
    const celularMask = computed(() => normalizarDdi(pessoa.celular_ddi) === '+55' ? 'celular' : null);
    const telefoneMaxlength = computed(() => normalizarDdi(pessoa.telefone_ddi) === '+55' ? 15 : 25);
    const celularMaxlength = computed(() => normalizarDdi(pessoa.celular_ddi) === '+55' ? 15 : 25);

    const showTelefoneDdiError = computed(() => !!errors.telefone_ddi && (touched.telefone_ddi || formTouched.value));
    const showTelefoneError = computed(() => !!errors.telefone && (touched.telefone || formTouched.value));
    const showCelularDdiError = computed(() => !!errors.celular_ddi && (touched.celular_ddi || formTouched.value));
    const showCelularError = computed(() => !!errors.celular && (touched.celular || formTouched.value));    

    const carregarEstados = async () => {
      try {
        const response = await axios.get(`/localidades/estados`);
        estados.value = response.data;
      } catch (error) {
        console.error('Erro ao carregar estados:', error);
      }
    };

    const carregarMunicipios = async () => {
      try {
        if (!pessoa.estado_cod) {
          municipios.value = [];
          return;
        }
        const response = await axios.get(`/localidades/municipios/${pessoa.estado_cod}`);
        municipios.value = response.data;
      } catch (error) {
        console.error('Erro ao carregar municípios:', error);
      }
    };

    // ====== NOVO: handler quando escolhe o responsável no modal ======
    const onSelecionarResponsavel = async ({ pessoa: respMin }) => {
      try {
        // Busca o registro COMPLETO
        const { data: resp } = await axios.get(`/cadastros/pessoas-fisicas/${respMin.id}`);

        // desliga watchers dependentes enquanto copiamos
        const prevFlag = initialLoadComplete.value;
        initialLoadComplete.value = false;

        // --- CONTATO
        pessoa.telefone_ddi = resp?.telefone_ddi ?? '+55';
        pessoa.telefone     = resp?.telefone ?? '';
        pessoa.celular_ddi  = resp?.celular_ddi ?? '+55';
        pessoa.celular      = resp?.celular  ?? '';
        pessoa.email        = resp?.email    ?? '';

        // --- ENDEREÇO
        pessoa.cep         = resp?.cep         ?? '';
        pessoa.endereco    = resp?.endereco    ?? '';
        pessoa.numero      = resp?.numero      ?? '';
        pessoa.complemento = resp?.complemento ?? '';
        pessoa.bairro      = resp?.bairro      ?? '';

        // Estado/Município
        pessoa.estado_cod = resp?.estado_cod ?? '';
        if (pessoa.estado_cod) {
          await carregarMunicipios();
          pessoa.municipio_cod = resp?.municipio_cod ?? '';
        } else {
          pessoa.municipio_cod = '';
          municipios.value = [];
        }

        // reativa watchers
        initialLoadComplete.value = prevFlag;
      } catch (err) {
        console.error('Falha ao carregar responsável completo:', err);
      }
    };
    // =====================================
    // ================================================================

    const salvarPessoa = async () => {
      if (isSubmitting.value) return; 
      isSubmitting.value = true;
      formTouched.value = true;

      touched.telefone = true;
      touched.celular = true;

      errors.telefone_ddi = validarDdi(pessoa.telefone_ddi, !!pessoa.telefone);
      errors.telefone = validarTelefone(pessoa.telefone);
      errors.celular_ddi = validarDdi(pessoa.celular_ddi, true);
      errors.celular = validarCelular(pessoa.celular);

      if (!isFormValid.value) {
        const mensagens = Object.values(errors).filter(Boolean);

        alert(
          mensagens.length
            ? `Não foi possível salvar:\n- ${mensagens.join('\n- ')}`
            : 'Não foi possível salvar. Verifique os campos obrigatórios.'
        );

        isSubmitting.value = false;
        return;
      }

      try {
        const url = pessoa.id
          ? `/cadastros/pessoas-fisicas/${pessoa.id}`
          : `/cadastros/pessoas-fisicas`;
        const method = pessoa.id ? 'put' : 'post';
        
        const response = await axios[method](url, pessoa);
        const novaPessoa = response.data;

        const returnUrl = route.query.returnUrl;
        const newPersonTipo = route.query.newPersonTipo;

        const queryRetorno = {
          newPersonId: novaPessoa.id,
          newPersonTipo: newPersonTipo || 'funcionario'
        };

        if (newPersonTipo && newPersonTipo !== 'funcionario' && route.query.prospeccaoId) {
          queryRetorno.prospeccaoId = route.query.prospeccaoId;
        }

        router.push({ 
          path: returnUrl || '/cadastros/pessoas-fisicas',
          query: queryRetorno
        });
        
      } catch (error) {
        console.error('Erro ao salvar pessoa física:', error.response?.data || error.message);
        alert(`Erro ao salvar pessoa: ${
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Erro desconhecido'
        }`);
      } finally {
        isSubmitting.value = false;
      }
    };

    const voltar = () => {
      const returnUrl = route.query.returnUrl;
      router.push({ path: returnUrl || '/cadastros/pessoas-fisicas' });
    };

    onMounted(async () => {
      await carregarEstados();
      const pessoaId = route.params.id;

      if (pessoaId) {
        try {
          const response = await axios.get(`/cadastros/pessoas-fisicas/${pessoaId}`);
          Object.assign(pessoa, response.data);
          pessoa.telefone_ddi = pessoa.telefone_ddi || '+55';
          pessoa.celular_ddi = pessoa.celular_ddi || '+55';

          if (pessoa.data_nascimento) {
            const date = new Date(pessoa.data_nascimento);
            pessoa.data_nascimento = isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
          }

          if (pessoa.estado_cod) {
            await carregarMunicipios();
          }
        } catch (error) {
          console.error('Erro ao carregar pessoa física:', error);
        }
      }
      initialLoadComplete.value = true;
    });

    return {
      pessoa,
      estados,
      municipios,
      errors,
      isFormValid,
      formTouched,
      isSubmitting,
      carregarEstados,
      carregarMunicipios,
      salvarPessoa,
      voltar,
      normalizeString,

      // modal
      modalResponsavel,
      onSelecionarResponsavel,
      initialLoadComplete,

      touched,
      ddiOptions,
      telefoneMask,
      celularMask,
      telefoneMaxlength,
      celularMaxlength,
      showTelefoneDdiError,
      showTelefoneError,
      showCelularDdiError,
      showCelularError,      
      onTelefoneInput,
      onCelularInput,
      onTelefoneBlur,
      onCelularBlur,      
    };
  },
};
</script>


<style scoped>
.phone-row {
  display: grid;
  grid-template-columns: 90px minmax(180px, 1fr);
  gap: 0.5rem;
  align-items: end;
  max-width: 420px;
}
</style>